import React, { useMemo } from 'react'
import { useLang } from '../lib/useLang'

function MetricsGrid({ metrics }) {
  const entries = Object.entries(metrics || {})
  if (!entries.length) return null

  return (
    <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
      {entries.map(([k, v]) => (
        <div
          key={k}
          className='flex flex-col items-center justify-center rounded-lg p-5 text-center'
          style={{
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid var(--hairline-strong)',
          }}
        >
          <span className='mb-2 text-[10px] font-semibold uppercase tracking-[0.16em]' style={{ color: 'var(--muted)' }}>{k}</span>
          <span className='text-2xl font-light tracking-tight' style={{ color: 'var(--ink)', lineHeight: 1 }}>
            {typeof v === 'number' ? parseFloat(v.toFixed(2)) : String(v)}
          </span>
        </div>
      ))}
    </div>
  )
}

function InfoCard({ icon, label, value }) {
  return (
    <div
      className='flex items-center gap-3 rounded-lg p-4'
      style={{
        background: 'rgba(255, 255, 255, 0.4)',
        border: '1px solid var(--hairline-strong)',
      }}
    >
      <div
        className='flex h-10 w-10 items-center justify-center rounded-md flex-shrink-0'
        style={{ background: 'rgba(255, 90, 31, 0.12)', color: 'var(--accent)' }}
      >
        {icon}
      </div>
      <div className='min-w-0'>
        <div className='text-[10px] font-semibold uppercase tracking-[0.16em]' style={{ color: 'var(--muted)' }}>{label}</div>
        <div className='mt-0.5 text-sm font-medium truncate' style={{ color: 'var(--ink)' }}>{value}</div>
      </div>
    </div>
  )
}

export default function StepDetails({ step, snapshot, status, onDownload }) {
  const { t } = useLang()
  const partials = snapshot?.partials || {}

  const details = useMemo(() => {
    if (!step) return { title: t('stepDetails.noStepSelected') }
    const d = { title: step }

    if (step === 'demand') {
      const p = partials.demand || {}
      const req = p.requirements || {}
      d.taskType = req.task_type || '-'
      d.target = req.target || '-'
      d.constraints = req.constraints
      try {
        const qs = Array.isArray(p.questions) ? p.questions : []
        if (qs.length) d.questions = qs
      } catch {}
    } else if (step === 'analyst') {
      const p = partials.analyst || {}
      const eda = p.eda_report || {}
      d.targetOk = String(eda.target_ok)
      d.dtypesCount = Object.keys(eda.dtypes || {}).length
    } else if (step === 'designer') {
      const p = partials.designer || {}
      const first = Array.isArray(p.models) && p.models[0]
      if (first) {
        d.summary = t('stepDetails.modelReady')
        d.model_uri = first.model_uri
        const m = {}
        for (const mm of first.metrics || []) {
          if (mm.metric_name) m[mm.metric_name] = mm.metric_value
        }
        d.metrics = m
      } else if (p.execution) {
        d.summary = p.plan ? t('stepDetails.planExecuted') : t('stepDetails.execution')
        d.model_uri = p.execution.model_uri
        d.metrics = p.execution.metrics
      }
    } else if (step === 'validator') {
      const st = status?.status
      let completed = st === 'COMPLETED'
      let failed = st === 'FAILED'
      try {
        if (!completed) {
          const hist = snapshot?.history || []
          failed = failed || (Array.isArray(hist) && hist.some(r => r?.ok === false || r?.error))
        }
      } catch {}
      d.validatorStatus = completed ? 'completed' : failed ? 'failed' : 'validating'
      d.summary = completed ? t('stepDetails.completed') : failed ? t('stepDetails.failed') : t('stepDetails.validating')
      const flat = partials.metrics || {}
      d.metrics = flat
    } else if (step === 'human_review') {
      const hr = snapshot?.pending_human_request
      d.summary = hr ? hr.request?.message || t('stepDetails.humanReviewRequired') : t('stepDetails.pausedForReview')
    }
    return d
  }, [step, snapshot, status, t])

  // Get execution log
  const executionLog = useMemo(() => {
    try {
      return snapshot?.partials?.[step]?.execution_log || []
    } catch {
      return []
    }
  }, [snapshot, step])

  const stepLabels = {
    demand: t('stepDetails.demand'),
    analyst: t('stepDetails.analyst'),
    designer: t('stepDetails.designer'),
    validator: t('stepDetails.validator'),
    human_review: t('stepDetails.humanReview'),
  }

  return (
    <div className='space-y-6'>
      {/* Step Header */}
      <div
        className='rounded-lg p-6 text-center'
        style={{
          background: 'rgba(255, 255, 255, 0.4)',
          border: '1px solid var(--hairline-strong)',
        }}
      >
        <div className='inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full'
             style={{ background: 'rgba(255, 90, 31, 0.1)', color: 'var(--accent)' }}>
          <span className='h-1.5 w-1.5 rounded-full' style={{ background: 'var(--accent)' }} />
          <span className='text-[10px] font-semibold uppercase tracking-[0.16em]'>{t('projectView.details')}</span>
        </div>
        <h3 className='text-xl font-medium tracking-tight capitalize' style={{ color: 'var(--ink)' }}>
          {stepLabels[step] || step}
        </h3>
        {details.summary && (
          <p className='mt-2 text-sm' style={{ color: 'var(--muted)' }}>{details.summary}</p>
        )}
        {details.validatorStatus && (
          <div className='mt-3 flex items-center justify-center gap-2'>
            <span
              className='h-2 w-2 rounded-full'
              style={{
                background:
                  details.validatorStatus === 'completed' ? 'var(--accent)' :
                  details.validatorStatus === 'failed' ? '#dc2626' :
                  'var(--accent)',
                animation: details.validatorStatus === 'completed' || details.validatorStatus === 'failed' ? 'none' : 'pulse 1.5s ease-in-out infinite',
              }}
            />
            <span className='text-xs capitalize font-medium' style={{ color: 'var(--ink)' }}>{details.validatorStatus}</span>
          </div>
        )}
      </div>

      {/* Demand Details */}
      {step === 'demand' && (
        <div className='grid gap-3 sm:grid-cols-2'>
          <InfoCard
            icon={
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <path d='M9 11l3 3L22 4' />
                <path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' />
              </svg>
            }
            label={t('stepDetails.taskType')}
            value={details.taskType}
          />
          <InfoCard
            icon={
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <circle cx='12' cy='12' r='10' />
                <path d='M12 16v-4M12 8h.01' />
              </svg>
            }
            label={t('stepDetails.target')}
            value={details.target}
          />
        </div>
      )}

      {/* Analyst Details */}
      {step === 'analyst' && (
        <div className='grid gap-3 sm:grid-cols-2'>
          <InfoCard
            icon={
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' />
                <polyline points='22,4 12,14.01 9,11.01' />
              </svg>
            }
            label={t('stepDetails.targetValid')}
            value={details.targetOk}
          />
          <InfoCard
            icon={
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <rect x='3' y='3' width='7' height='7' />
                <rect x='14' y='3' width='7' height='7' />
                <rect x='14' y='14' width='7' height='7' />
                <rect x='3' y='14' width='7' height='7' />
              </svg>
            }
            label={t('stepDetails.dataTypes')}
            value={t('stepDetails.columns', { count: details.dtypesCount })}
          />
        </div>
      )}

      {/* Questions */}
      {Array.isArray(details.questions) && details.questions.length > 0 && (
        <div
          className='rounded-lg p-5'
          style={{
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid var(--hairline-strong)',
          }}
        >
          <h4 className='mb-3 text-[10px] font-semibold uppercase tracking-[0.16em]' style={{ color: 'var(--muted)' }}>{t('stepDetails.questions')}</h4>
          <ul className='space-y-2.5'>
            {details.questions.map((q, i) => (
              <li key={i} className='flex items-start gap-2.5 text-sm' style={{ color: 'rgba(14, 14, 16, 0.78)' }}>
                <span className='mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full' style={{ background: 'var(--accent)' }} />
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Links */}
      {details.links && details.links.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {details.links.map((l, i) => (
            <a
              key={i}
              href={l.url}
              target='_blank'
              rel='noreferrer'
              className='flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-accent transition-all hover:bg-accent/15'
              style={{ border: '1px solid rgba(255, 90, 31, 0.2)' }}
            >
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <path d='M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' />
                <polyline points='15,3 21,3 21,9' />
                <line x1='10' y1='14' x2='21' y2='3' />
              </svg>
              {l.label}
            </a>
          ))}
        </div>
      )}

      {/* Metrics */}
      {details.metrics && Object.keys(details.metrics).length > 0 && (
        <div>
          <h4 className='mb-3 text-[10px] font-semibold uppercase tracking-[0.16em]' style={{ color: 'var(--muted)' }}>{t('stepDetails.metrics')}</h4>
          <MetricsGrid metrics={details.metrics} />
        </div>
      )}

      {/* Execution Log */}
      {Array.isArray(executionLog) && executionLog.length > 0 && (
        <div
          className='rounded-lg p-5'
          style={{
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid var(--hairline-strong)',
          }}
        >
          <h4 className='mb-3 text-[10px] font-semibold uppercase tracking-[0.16em]' style={{ color: 'var(--muted)' }}>{t('stepDetails.executionLog')}</h4>
          <div className='max-h-48 space-y-2 overflow-y-auto' style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}>
            {executionLog.map((it, idx) => (
              <div key={idx} className='rounded-md p-3' style={{ background: 'rgba(255, 255, 255, 0.5)', border: '1px solid var(--hairline)' }}>
                <div className='mb-1 font-mono text-[10px]' style={{ color: 'var(--accent)' }}>{it.t}</div>
                <div className='whitespace-pre-wrap text-xs' style={{ color: 'rgba(14, 14, 16, 0.78)' }}>{it.notes}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Download Button */}
      {status?.done && (snapshot?.run_meta?.workflow_id || status?.artifacts?.model_uri) && (
        <button
          onClick={() => onDownload?.(snapshot?.run_meta?.workflow_id)}
          className='flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium transition-all'
          style={{
            background: 'var(--ink)',
            color: 'var(--cream)',
            border: 'none',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#1f1f24'
            e.currentTarget.style.transform = 'translateY(-1px)'
            e.currentTarget.style.boxShadow = '0 10px 30px -10px rgba(14, 14, 16, 0.35)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'var(--ink)'
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = 'none'
          }}
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
            <polyline points='7,10 12,15 17,10' />
            <line x1='12' y1='15' x2='12' y2='3' />
          </svg>
          {t('stepDetails.downloadModel')}
        </button>
      )}
    </div>
  )
}
